import React from "react";

import { Badge2 } from "@/ui2/atoms/Badge";
import { cn } from "@/lib/helpers/cn";
import dayjs from "@/lib/helpers/dayjs";
import InfoIcon from "@/assets/images/icons2/icon--information.svg?component";
import type { Note } from "@/core/types";
import { Tooltip2 } from "@/components/UI2/molecules/Tooltip";

import type { DateFields } from "../helpers/format-completed-date-time";
import { formatCompletedDateTime } from "../helpers/format-completed-date-time";
import { resolveIcaDates } from "../helpers/resolve-ica-dates";

type NoteMetaProps = {
  className?: string;
  note: Note;
  showSignature?: boolean;
};

export function NoteMetaData({ className, note, showSignature }: NoteMetaProps) {
  const content = getNoteMetaContent({ note, showSignature });

  if (content === null) return null;

  return (
    <ul
      className={cn("tw:flex tw:items-start tw:justify-center tw:gap-1 tw:lg:flex-col", className)}
    >
      {content}
    </ul>
  );
}

export function getNoteMetaContent({
  note,
  showSignature = true,
}: {
  note: Note;
  showSignature?: boolean;
}): React.ReactNode {
  if (
    (note.note_type === "Care Management" || note.note_type === "Advanced Care Activity") &&
    note.note_state === "draft"
  ) {
    return <DraftBadge />;
  }

  if (note.note_state === "rejected") return <CosignRejectedBadge />;

  if (note.is_cosign_required && ["pending", "resubmitted"].includes(note.note_state))
    return <PendingCosignBadge />;

  if (note.note_type === "Care Management" || note.note_type === "Advanced Care Activity") {
    if (!showSignature) return null;

    return (
      <SignatureDateMeta
        signature={note.signature}
        {...formatCompletedDateTime(note.completed_date_time)}
      />
    );
  }

  if (note.note_type === "Initial Clinical Assessment") {
    if (!showSignature) return null;

    return <SignatureDateMeta {...resolveIcaDates(note)} signature={note.signature} />;
  }

  if (note.version === 5 || note.version === 6) {
    if (!showSignature) return null;

    return (
      <SignatureDateMeta
        displayDate={dayjs(note.session_date, "MM/DD/YYYY").format("MM/DD/YYYY")}
        displayTime={dayjs(note.session_from, "hh:mm").format("h:mm a")}
        isoDate={dayjs(note.session_date, "MM/DD/YYYY").toISOString()}
        signature={note.signature}
      />
    );
  }

  return note.note_state === "draft" ? <DraftBadge /> : null;
}

type SignatureDateMetaProps = DateFields & {
  signature?: string;
};

function SignatureDateMeta(props: SignatureDateMetaProps) {
  const { displayDate, displayTime, isoDate, signature } = props;

  return (
    <li className="body-sm tw:flex tw:flex-col">
      <span>{signature}</span>
      <span className="tw:contents tw:uppercase">
        <time dateTime={isoDate ?? undefined}>{displayDate}</time>
        <time dateTime={isoDate ?? undefined}>{displayTime}</time>
      </span>
    </li>
  );
}

function DraftBadge() {
  return (
    <li className="tw:contents">
      <Badge2 appearance="outline" size="md" variant="bright-purple">
        Draft
      </Badge2>
    </li>
  );
}

function PendingCosignBadge() {
  return (
    <li className="tw:contents">
      <Tooltip2
        className="tw:min-w-60"
        icon={<InfoIcon />}
        text="This note is awaiting co-sign from your supervisor."
      >
        <Badge2 appearance="outline" size="md" variant="warning">
          Pending Co-Sign
        </Badge2>
      </Tooltip2>
    </li>
  );
}

function CosignRejectedBadge() {
  return (
    <li className="tw:contents">
      <Tooltip2
        className="tw:min-w-60"
        icon={<InfoIcon />}
        text="Returned by your supervisor. Please review the feedback and revise the note."
      >
        <Badge2 appearance="outline" size="md" variant="error">
          Returned
        </Badge2>
      </Tooltip2>
    </li>
  );
}
